import axios from "axios";
import { FounderCounts } from "../lib/founderCounts";
import { IEstablishment, EstablishmentReportType, IEstablishmentReport, EstablishmentReportCounts, NafReportStat } from "./establishment";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
});

export interface FranchiseDoc {
    _id: string;
    name: string;
    logoUrl: string | null;
    imageUrl: string | null;
    description: string | null;
    website: string | null;
    /** Last time an admin ticked the network as reviewed in /bo/franchises; null = never. */
    lastAnalyzedAt?: string | null;
    createdAt?: string;
    establishmentCount?: number;
    recentNewsCount?: number;
    latestNewsCategory?: "opening" | "closing" | "ownership" | "recruitment" | null;
    topCategory?: string | null;
    /** Distinct operators running at least one active site of this network (chain executives excluded). */
    franchiseeCount?: number;
    /** Other brands run by this network's franchisees, most shared first (≥ 2 shared operators). */
    associated?: AssociatedFranchise[];
}

export interface FranchiseBrand {
    _id: string;
    name: string;
    logoUrl: string | null;
    website: string | null;
}

export interface AssociatedFranchise extends FranchiseBrand {
    sharedFranchisees: number;
}

/**
 * Every franchise, unfiltered — including those with no establishment yet, which the
 * assignment pickers need to keep selectable. For the browsable directory use
 * `browseFranchises`, which filters and orders server-side.
 */
export const getFranchises = async (): Promise<FranchiseDoc[]> => {
    const response = await axios.get(`${API_URL}/franchises`, {
        headers: authHeaders(),
    });
    return response.data;
};

export interface FranchiseBrowseParams {
    search?: string;
    category?: string;
    minEstablishments?: number;
    minRecentNews?: number;
    activityType?: string;
    sort?: "count" | "activity" | "name";
    page?: number;
    limit?: number;
}

export interface FranchiseBrowseResult {
    items: FranchiseDoc[];
    total: number;
    page: number;
    totalPages: number;
    limit: number;
    minEstablishments: number;
    categories: { cat: string; count: number }[];
    stats: {
        franchises: number;
        totalEstablishments: number;
        totalNews3Months: number;
        openings: number;
    };
}

/**
 * The franchise directory. Filtering, ordering and paging all happen server-side over the whole
 * collection, so page 2 continues page 1 instead of being an independently filtered slice.
 */
export const browseFranchises = async (params: FranchiseBrowseParams = {}): Promise<FranchiseBrowseResult> => {
    const response = await axios.get(`${API_URL}/franchises/browse`, {
        headers: authHeaders(),
        params,
    });
    return response.data;
};

export interface FranchiseNewsItem {
    _id: string;
    title: string;
    link: string;
    city: string | null;
    status: string;
    category: string;
    published: string;
    image: string | null;
    source: { name: string; domain: string; favicon: string | null };
}

export interface FranchiseHygieneSignal {
    type: "hygiene_control";
    date?: string;
    metadata?: {
        score_label?: string;
        score_code?: number;
        evaluation?: string;
        follow_up_type?: string;
        inspection_number?: string;
    };
}

export interface FranchiseEstablishment {
    _id: string;
    name: string;
    franchise?: string | null;
    address?: { city?: string | null; postcode?: string | null };
    operations?: { opening_date?: string | null };
    phone?: string | null;
    email?: string | null;
    ratings?: { source_summary?: Array<{ source: string; rating_avg?: number; rating_count?: number; url?: string }> };
    peoples?: Array<{ _id?: string; name?: string; enrichments?: any[] }>;
    categories?: string[];
    subCategories?: string[];
    signals?: Array<FranchiseHygieneSignal | { type: string; date?: string; metadata?: any }>;
}

/** The network read as a market — computed over every establishment, not one page of the table. */
export interface FranchiseIntel {
    totalSites: number;
    activeSites: number;
    franchisees: number;
    multiUnit: number;
    multiBrand: number;
    departments: { code: string; count: number }[];
    openingsByYear: { year: number; count: number }[];
    associated: AssociatedFranchise[];
}

export interface FranchiseDetail {
    franchise: FranchiseDoc;
    news: FranchiseNewsItem[];
    /** Totals for the tab bar; the rows come from `getFranchiseEstablishments`, a page at a time. */
    counts: { establishments: number; hygiene: number };
    intel: FranchiseIntel;
}

export const getFranchise = async (id: string): Promise<FranchiseDetail> => {
    const response = await axios.get(`${API_URL}/franchises/${id}`, {
        headers: authHeaders(),
    });
    return response.data;
};

export type FranchiseEstablishmentView = "all" | "hygiene";
/** Hygiene view order: latest control first (`recent`, default) or worst latest result first (`worst`). */
export type FranchiseHygieneSort = "recent" | "worst";
/** Latest hygiene result, as the `result` filter and `resultCounts` name it. */
export type FranchiseHygieneResult = "urgent" | "improve" | "ok" | "very_good";
/** Sites run by a gérant with ≥ 2 open establishments anywhere (`multi_site`) or ≥ 2 brands (`multi_brand`). */
export type FranchiseOwnerFilter = "multi_site" | "multi_brand";

export interface FranchiseEstablishmentPage {
    items: FranchiseEstablishment[];
    total: number;
    page: number;
    totalPages: number;
    limit: number;
    /** Sites each owner filter would keep in the current view, whatever filter is applied. */
    ownerCounts: Record<FranchiseOwnerFilter, number>;
    /** Hygiene view only: sites per latest result, whatever `result` filter is applied. */
    resultCounts?: Record<FranchiseHygieneResult, number>;
}

/** One page of a franchise's establishments: `all` newest opening first, `hygiene` by its latest control (`sort`). */
export const getFranchiseEstablishments = async (
    id: string,
    params: {
        view?: FranchiseEstablishmentView;
        owners?: FranchiseOwnerFilter;
        sort?: FranchiseHygieneSort;
        result?: FranchiseHygieneResult;
        page?: number;
        limit?: number;
    } = {}
): Promise<FranchiseEstablishmentPage> => {
    const response = await axios.get(`${API_URL}/franchises/${id}/establishments`, {
        headers: authHeaders(),
        params,
    });
    return response.data;
};

export const createFranchise = async (name: string): Promise<FranchiseDoc> => {
    const response = await axios.post(`${API_URL}/franchises`, { name }, {
        headers: authHeaders(),
    });
    return response.data;
};

export const updateFranchise = async (id: string, data: Partial<Omit<FranchiseDoc, "_id" | "name">>): Promise<FranchiseDoc> => {
    const response = await axios.patch(`${API_URL}/franchises/${id}`, data, {
        headers: authHeaders(),
    });
    return response.data;
};

/** Stamps now as the franchise's last analysis, or clears it with `analyzed: false`. */
export const setFranchiseAnalyzed = async (id: string, analyzed: boolean): Promise<{ lastAnalyzedAt: string | null }> => {
    const response = await axios.put(`${API_URL}/franchises/${id}/analyzed`, { analyzed }, {
        headers: authHeaders(),
    });
    return response.data;
};

export const getEstablishment = async (id: string): Promise<IEstablishment> => {
    const response = await axios.get(`${API_URL}/establishments/${id}`, {
        headers: authHeaders(),
    });
    return response.data;
};

export const getEstablishments = async (params?: any): Promise<{
    establishments: IEstablishment[];
    total: number;
    page: number;
    totalPages: number;
}> => {
    const response = await axios.get(`${API_URL}/establishments`, {
        headers: authHeaders(),
        params,
    });
    return response.data;
};

// Count-only variant of getEstablishments: same filters, returns just { total, totalPages }.
// The exact count over a broad filter is the slow part of the list endpoint, so pages fetch
// rows with deferTotal: "true" and fill the count in from this parallel call when it lands.
export const getEstablishmentsCount = async (params?: any): Promise<{ total: number; totalPages: number }> => {
    const response = await axios.get(`${API_URL}/establishments`, {
        headers: authHeaders(),
        params: { ...params, countOnly: "true" },
    });
    return response.data;
};

// Multi-établissements chips: the total per owner opening rank for these filters, in one
// cached aggregation. `all` is the count without any rank, so it doubles as the folder total.
export type OpeningRankCounts = { all: number; "2": number; "3": number; "4": number; "5+": number };

export const getEstablishmentRankCounts = async (params?: any): Promise<OpeningRankCounts> => {
    const response = await axios.get(`${API_URL}/establishments`, {
        headers: authHeaders(),
        params: { ...params, rankCounts: "true" },
    });
    return response.data.counts;
};

export const getEstablishmentStats = async (): Promise<Record<string, number>> => {
    const response = await axios.get(`${API_URL}/establishments/stats/by-signal-type`, {
        headers: authHeaders(),
    });
    return response.data;
};

export const getEstablishmentTags = async (): Promise<{ categories: string[]; subCategories: string[] }> => {
    const response = await axios.get(`${API_URL}/establishments/tags/unique`, {
        headers: authHeaders(),
    });
    return response.data;
};

export interface NafCodeOption {
    code: string;
    description?: string;
}

export const getUniqueNafCodes = async (): Promise<NafCodeOption[]> => {
    const response = await axios.get(`${API_URL}/establishments/naf/unique`, {
        headers: authHeaders(),
    });
    return response.data;
};

export const getNafRelatedTags = async (code: string): Promise<{ categories: string[]; subCategories: string[] }> => {
    const response = await axios.get(`${API_URL}/establishments/naf/${encodeURIComponent(code)}/related-tags`, {
        headers: authHeaders(),
    });
    return response.data;
};

export const saveEstablishment = async (id: string): Promise<{ saved: boolean }> => {
    const response = await axios.post(`${API_URL}/establishments/${id}/save`, {}, {
        headers: authHeaders(),
    });
    return response.data;
};

export interface EstablishmentMyContext {
    /** The rep's own LightCRM prospect linked to this establishment, if any. */
    prospect: {
        _id: string;
        status: string;
        notes?: string;
        nextAction?: string;
        nextActionAt?: string;
        lastContactAt?: string;
        updatedAt?: string;
    } | null;
    /** The rep's lists this establishment is pinned in. */
    lists: Array<{ _id: string; name: string }>;
    /** Approved press linked to the establishment, newest first. */
    news: Array<{ _id: string; title: string; link: string; published: string; category?: string; source?: { name?: string } }>;
}

export const getEstablishmentMyContext = async (id: string): Promise<EstablishmentMyContext> => {
    const response = await axios.get(`${API_URL}/establishments/${id}/my-context`, {
        headers: authHeaders(),
    });
    return response.data;
};

export const viewEstablishment = async (id: string): Promise<void> => {
    await axios.post(`${API_URL}/establishments/${id}/view`, {}, {
        headers: authHeaders(),
    });
};

export interface BoddacAnnouncement {
    _id?: string;
    date?: string;
    announcementType?: string;
    announcementFamily?: string;
    tribunal?: string;
    company?: {
        denomination?: string;
        commercialName?: string;
        legalForm?: string;
        registrationNumber?: string;
        capital?: { amount: string; currency: string };
        address?: { streetNumber?: string; streetType?: string; streetName?: string; postalCode?: string; city?: string };
    };
    founders?: Array<{ fullName?: string; prenom?: string; nom?: string; qualite?: string; dateNaissance?: string; nationalite?: string }>;
    legalActs?: { creationDate?: string; registrationDate?: string; activityStartDate?: string; creationCategory?: string };
    isRadiation?: boolean;
    modificationDescription?: string | null;
    buyer?: { siren?: string; denomination?: string; establishmentId?: string | null } | null;
    previousOwner?: { siren?: string; denomination?: string } | null;
}

export interface BoddacHistoryEstablishment {
    _id: string;
    name: string;
    siren?: string;
    city?: string;
    bodaccCount: number;
    latestDate?: string;
    announcements: BoddacAnnouncement[];
}

export const getBodaccAnnouncementTypes = async (): Promise<string[]> => {
    const response = await axios.get(`${API_URL}/establishments/admin/bodacc-history/announcement-types`, {
        headers: authHeaders(),
    });
    return response.data.types;
};

export const getBodaccHistory = async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    name?: string;
    siren?: string;
    announcementType?: string;
    minCount?: number;
    maxCount?: number;
}): Promise<{
    establishments: BoddacHistoryEstablishment[];
    total: number;
    page: number;
    totalPages: number;
}> => {
    const response = await axios.get(`${API_URL}/establishments/admin/bodacc-history`, {
        headers: authHeaders(),
        params,
    });
    return response.data;
};


export const getDatabaseEstablishments = async (params?: any): Promise<{
    establishments: IEstablishment[];
    total: number;
    page: number;
    totalPages: number;
}> => {
    const response = await axios.get(`${API_URL}/establishments/database`, {
        headers: authHeaders(),
        params,
    });
    return response.data;
};

export const exportDatabaseEstablishments = async (params?: Record<string, any>): Promise<void> => {
    const query = new URLSearchParams();
    if (params) {
        for (const [k, v] of Object.entries(params)) {
            if (v != null && v !== "") query.append(k, String(v));
        }
    }
    const url = `${API_URL}/establishments/database/export?${query.toString()}`;
    const response = await fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
    if (!response.ok) throw new Error("Export failed");
    const blob = await response.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `export-etablissements-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
};

export const getUntaggedEstablishments = async (params?: {
    period?: "week" | "month" | "all";
    page?: number;
    limit?: number;
    signalType?: string;
}): Promise<{
    establishments: IEstablishment[];
    total: number;
    totalUntagged: number;
    totalAll: number;
    page: number;
    totalPages: number;
}> => {
    const response = await axios.get(`${API_URL}/establishments/admin/untagged`, {
        headers: authHeaders(),
        params,
    });
    return response.data;
};

export const getFounderEstablishmentCounts = async (
    personIds: string[]
): Promise<Record<string, FounderCounts>> => {
    const response = await axios.get(`${API_URL}/establishments/founder-counts`, {
        headers: authHeaders(),
        params: { ids: personIds.join(",") },
    });
    return response.data;
};

export const searchEstablishmentsAdmin = async (params?: {
    q?: string;
    page?: number;
    limit?: number;
    noFranchise?: boolean;
}): Promise<{
    establishments: IEstablishment[];
    total: number;
    page: number;
    totalPages: number;
}> => {
    const response = await axios.get(`${API_URL}/establishments/admin/search`, {
        headers: authHeaders(),
        params,
    });
    return response.data;
};

export const getMultiSiteFounderNeighbors = async (founderName: string): Promise<{
    prev: string | null;
    next: string | null;
    total: number;
    index: number;
}> => {
    const response = await axios.get(`${API_URL}/establishments/multi-site-founders/neighbors`, {
        headers: authHeaders(),
        params: { founderName },
    });
    return response.data;
};

export const bulkUpdateEstablishmentTags = async (
    ids: string[],
    tags: { categories: string[]; subCategories: string[] }
): Promise<{ modifiedCount: number }> => {
    const response = await axios.patch(
        `${API_URL}/establishments/admin/bulk-tags`,
        { ids, ...tags },
        { headers: authHeaders() }
    );
    return response.data;
};

export const getUnfranchisedEstablishments = async (params?: {
    period?: "week" | "month" | "all";
    page?: number;
    limit?: number;
    q?: string;
}): Promise<{
    establishments: IEstablishment[];
    total: number;
    totalUnfranchised: number;
    totalAll: number;
    page: number;
    totalPages: number;
}> => {
    const response = await axios.get(`${API_URL}/establishments/admin/unfranchised`, {
        headers: authHeaders(),
        params,
    });
    return response.data;
};

export const bulkUpdateEstablishmentFranchise = async (
    ids: string[],
    franchiseId: string | null
): Promise<{ modifiedCount: number }> => {
    const response = await axios.patch(
        `${API_URL}/establishments/admin/bulk-franchise`,
        { ids, franchiseId },
        { headers: authHeaders() }
    );
    return response.data;
};

export const updateEstablishmentFranchise = async (
    id: string,
    franchiseId: string | null
): Promise<IEstablishment> => {
    const response = await axios.patch(
        `${API_URL}/establishments/admin/${id}/franchise`,
        { franchiseId },
        { headers: authHeaders() }
    );
    return response.data;
};

export interface StatusWeeklyData {
    label: string;
    verified: number;
    closed: number;
    created: number;
    enriched: number;
    googlePlaceDone: number;
    googlePlacePending: number;
    linkedinPending: number;
}

export interface StatusVerificationInfo {
    name: string;
    location: string | null;
    date: string;
}

export interface EstablishmentStatusStats {
    verifiedThisWeek: number;
    verifiedThisWeekChange: number | null;
    closedDetectedThisWeek: number;
    notVerifiedOver7Days: number;
    totalActiveTracked: number;
    inseeNotFound: number;
    mostRecentVerification: StatusVerificationInfo | null;
    oldestVerification: StatusVerificationInfo | null;
    weeklyData: StatusWeeklyData[];
}

export interface SirenCheckResult {
    establishment: { _id: string; name: string };
    before: { status: string | null; is_active: boolean | null; last_status_update: string | null; insee_not_found: boolean };
    after: { status: string | null; is_active: boolean | null; last_status_update: string | null; insee_not_found: boolean };
    changed: boolean;
}

export const checkSirenStatus = async (siren: string): Promise<SirenCheckResult> => {
    const response = await axios.post(
        `${API_URL}/establishments/admin/check-siren`,
        { siren },
        { headers: authHeaders() }
    );
    return response.data;
};

export const checkSiretStatus = async (siret: string): Promise<SirenCheckResult> => {
    const response = await axios.post(
        `${API_URL}/establishments/admin/check-siren`,
        { siret },
        { headers: authHeaders() }
    );
    return response.data;
};

export interface SirenEstablishmentsResult {
    siren: string;
    establishments: IEstablishment[];
}

export const getEstablishmentsBySiren = async (siren: string): Promise<SirenEstablishmentsResult> => {
    const response = await axios.get(`${API_URL}/establishments/by-siren/${siren}`, {
        headers: authHeaders(),
    });
    return response.data;
};

export const getEstablishmentStatusStats = async (): Promise<EstablishmentStatusStats> => {
    const response = await axios.get(`${API_URL}/establishments/admin/status-stats`, {
        headers: authHeaders(),
    });
    return response.data;
};

export interface ClosingCheckerPendingItem {
    _id: string;
    name: string;
    legal_name?: string;
    address?: { city?: string };
    registration?: { siren?: string; last_status_update?: string | null };
}

export interface ClosingCheckerPendingResponse {
    data: ClosingCheckerPendingItem[];
    total: number;
    page: number;
    totalPages: number;
}

export const getClosingCheckerPending = async (page = 1, limit = 50): Promise<ClosingCheckerPendingResponse> => {
    const response = await axios.get(`${API_URL}/enrichment-rules/closing-checker-pending`, {
        params: { page, limit },
        headers: authHeaders(),
    });
    return response.data;
};

export interface AlimConfianceImportStats {
    total: number;
    created: number;
    updated: number;
    skipped: number;
    eventsCreated: number;
}

export const importAlimConfiance = async (date: string): Promise<AlimConfianceImportStats> => {
    const response = await axios.post(
        `${API_URL}/admin/alim-confiance/import`,
        { date },
        { headers: authHeaders() }
    );
    return response.data;
};

export const updateEstablishmentTags = async (
    id: string,
    tags: { categories: string[]; subCategories: string[] }
): Promise<IEstablishment> => {
    const response = await axios.patch(`${API_URL}/establishments/admin/${id}/tags`, tags, {
        headers: authHeaders(),
    });
    return response.data;
};


// ─── Report API ────────────────────────────────────────────────────────────

export const reportEstablishment = async (
    id: string,
    reportType: EstablishmentReportType,
    comment?: string
): Promise<{ message: string; report: IEstablishmentReport }> => {
    const response = await axios.post(
        `${API_URL}/establishments/${id}/report`,
        { reportType, comment },
        { headers: authHeaders() }
    );
    return response.data;
};

/**
 * Reports filed by every user on the given establishments, batched for one table page.
 * Establishments nobody reported are absent from the response, not zero-filled.
 */
export const getEstablishmentReportCounts = async (
    ids: string[]
): Promise<Record<string, EstablishmentReportCounts>> => {
    const response = await axios.get(`${API_URL}/establishments/report-counts`, {
        headers: authHeaders(),
        params: { ids: ids.join(",") },
    });
    return response.data;
};

export const removeEstablishmentReport = async (id: string): Promise<{ message: string }> => {
    const response = await axios.delete(`${API_URL}/establishments/${id}/report`, {
        headers: authHeaders(),
    });
    return response.data;
};

// ─── Owner enrichment (admin only) ──────────────────────────────────────────

export interface FounderInfo {
    nom?: string;
    prenom?: string;
    fullName?: string;
    firstName?: string;
    lastName?: string;
    age?: number;
    dateNaissance?: string;
    qualite?: string;
    role?: string;
    type?: "physical" | "moral";
}

export interface CheckOwnerSourceResult {
    name: string;
    ok: boolean;
    count?: number;
    error?: string;
}

export interface CheckOwnerResult {
    establishmentId: string;
    siren: string;
    status: "ok" | "no_data" | "error";
    founders: FounderInfo[];
    sources: CheckOwnerSourceResult[];
    signalCreated: boolean;
    linkedPersonIds: string[];
    multiSiteTagged: boolean;
    multiSiteOwners: { personId: string; otherEstablishmentCount: number }[];
    rawApiResponses: Record<string, any>;
}

export const checkEstablishmentOwner = async (id: string): Promise<CheckOwnerResult> => {
    const response = await axios.post(
        `${API_URL}/establishments/admin/${id}/check-owner`,
        {},
        { headers: authHeaders() }
    );
    return response.data;
};

export const deleteEstablishmentReport = async (
    establishmentId: string,
    userId: string
): Promise<{ message: string }> => {
    const response = await axios.delete(
        `${API_URL}/establishments/${establishmentId}/reports/${userId}`,
        { headers: authHeaders() }
    );
    return response.data;
};

export interface AdminReportsResponse {
    reports: IEstablishmentReport[];
    total: number;
    page: number;
    totalPages: number;
}

export const hideEstablishment = async (establishmentId: string): Promise<{ message: string }> => {
    const response = await axios.patch(
        `${API_URL}/establishments/admin/${establishmentId}/hide`,
        {},
        { headers: authHeaders() }
    );
    return response.data;
};

export const unhideEstablishment = async (establishmentId: string): Promise<{ message: string }> => {
    const response = await axios.patch(
        `${API_URL}/establishments/admin/${establishmentId}/unhide`,
        {},
        { headers: authHeaders() }
    );
    return response.data;
};

export interface OverviewStats {
    establishments: {
        total: number;
        displayed: number;
        hidden: number;
        closed: number;
        withInstagram: number;
        withEmail: number;
        withTags: number;
        withSubTags: number;
        phone: {
            total: number;
            mobile: number;
            other: number;
        };
    };
    people: {
        linkedinApproved: number;
        personalEmailsApproved: number;
        personalEmailsAutoApproved: number;
        personalEmailsManualApproved: number;
    };
}

export interface OverviewStatsFilters {
    signalType?: string;
    noSignals?: boolean;
    isFranchise?: boolean;
    category?: string;
}

export const getSignalTypeCounts = async (): Promise<Record<string, number>> => {
    const response = await axios.get(`${API_URL}/establishments/stats/by-signal-type`, {
        headers: authHeaders(),
    });
    return response.data;
};

export const getOverviewStats = async (filters?: OverviewStatsFilters): Promise<OverviewStats> => {
    const params = new URLSearchParams();
    if (filters?.signalType) params.append("signalType", filters.signalType);
    if (filters?.noSignals) params.append("noSignals", "true");
    if (filters?.isFranchise) params.append("isFranchise", "true");
    if (filters?.category) params.append("category", filters.category);
    const response = await axios.get(`${API_URL}/establishments/admin/overview-stats?${params.toString()}`, {
        headers: authHeaders(),
    });
    return response.data;
};


export interface KpiSnapshot {
    date: string;
    establishments: {
        total: number;
        displayed: number;
        hidden: number;
        closed: number;
        withInstagram: number;
        withEmail: number;
        withTags: number;
        withSubTags: number;
        phone: { total: number; mobile: number; other: number };
    };
    people: {
        linkedinApproved: number;
        personalEmailsApproved: number;
        personalEmailsAutoApproved: number;
        personalEmailsManualApproved: number;
    };
}

export const getKpiHistory = async (): Promise<KpiSnapshot[]> => {
    const response = await axios.get(`${API_URL}/establishments/admin/kpi-history`, {
        headers: authHeaders(),
    });
    return response.data;
};

export const getAdminReports = async (params?: {
    reportType?: EstablishmentReportType;
    nafCode?: string;
    page?: number;
    limit?: number;
    showHidden?: boolean;
}): Promise<AdminReportsResponse> => {
    const searchParams = new URLSearchParams();
    if (params?.reportType) searchParams.append("reportType", params.reportType);
    if (params?.nafCode) searchParams.append("nafCode", params.nafCode);
    if (params?.page) searchParams.append("page", String(params.page));
    if (params?.limit) searchParams.append("limit", String(params.limit));
    if (params?.showHidden) searchParams.append("showHidden", "true");
    const response = await axios.get(
        `${API_URL}/establishments/admin/reports?${searchParams.toString()}`,
        { headers: authHeaders() }
    );
    return response.data;
};

export const getAdminReportsNafStats = async (params?: {
    showHidden?: boolean;
}): Promise<NafReportStat[]> => {
    const searchParams = new URLSearchParams();
    if (params?.showHidden) searchParams.append("showHidden", "true");
    const response = await axios.get(
        `${API_URL}/establishments/admin/reports/naf-stats?${searchParams.toString()}`,
        { headers: authHeaders() }
    );
    return response.data;
};
